/**
 * Applies padding to bounds
 * 
 * @param bounds 
 * @param resolution 
 * @param paddingPx 
 * 
 * @returns 
 */
export function padBounds(bounds: number[], resolution: number, paddingPx: number[]){
    bounds = lonLatToMercator(bounds);
    bounds[0] += (paddingPx[0] * resolution);
    bounds[1] += (paddingPx[3] * resolution); // Y axis is reversed
    bounds[2] -= (paddingPx[2] * resolution);
    bounds[3] -= (paddingPx[1] * resolution);
    bounds = mercatorToLonLat(bounds);

    return bounds;
}

/**
 * Applies padding to LonLat point
 * 
 * @param lon 
 * @param lat 
 * @param resolution 
 * @param paddingPx 
 * 
 * @returns 
 */
export function padLonLat(lon: number, lat: number, resolution: number, paddingPx: number[]): number[] {
    const x = (paddingPx[2] - paddingPx[0]) * resolution / 2;
    const y = (paddingPx[1] - paddingPx[3]) * resolution / 2;
    const offset = mercatorToLonLat([x, y]);

    lon += offset[0];
    lat += offset[1];

    return [lon, lat];
}

/**
 * Convert LonLat coordinates to pixel coordinates.
 * 
 * @param coordinates
 * @param viewBounds 
 * @param resolution 
 * 
 * @returns 
 */
export function lonLatToPx(coordinates: number[], viewBounds: number[], resolution: number): number[] {
    coordinates = lonLatToMercator(coordinates);

    let x = coordinates[0];
    let y = coordinates[1];

    x -= viewBounds[0];
    y -= viewBounds[3];

    x = (x) / resolution;
    y = -((y) / resolution);

    return [x, y];
}

/**
 * Converts Mercator coordinates to Longitude/Latitude.
 * 
 * @param mercatorCoords
 */
export function mercatorToLonLat(mercatorCoords: number[]): number[] {
    const lonLatCoords = [];

    for (let i = 0; i < mercatorCoords.length; ) {
        lonLatCoords.push(x2lon(mercatorCoords[i++]), y2lat(mercatorCoords[i++]));
    }

    return lonLatCoords;
}

function x2lon(x: number) {
    x = x * 180 / 20037508.3427892;
    return x;
    // return round(x, 7);
}

function y2lat(y: number) {
    y = (y / 20037508.3427892) * 180;
    y = 180 / Math.PI * (2 * Math.atan(Math.exp(y * Math.PI / 180)) - Math.PI / 2);
    return y;
    // return round(y, 7);
}

/**
 * Converts Longitude/Latitude coordinates to Mercator
 * 
 * @param lonLatCoords
 */
export function lonLatToMercator(lonLatCoords: number[]): number[] {
    const mercatorCoords: number[] = [];
    for (let i = 0; i < lonLatCoords.length; ) {
        const lon = lonLatCoords[i++];
        const lat = lonLatCoords[i++];
        if (lon == 0 && lat == 0) {
            // it might not be an err. e.g. a layer with no geoms so has [0,0] lonlat bounds
            es.log.warn('coordinates at 0,0 are probably wrong (empty dataset?)');
        }
        const x = lon2x(lon);
        const y = lat2y(lat);
        if (x == null || y == null || isNaN(x) || isNaN(y)) {
            throw new Error("toMercator error causing NaN result: " + JSON.stringify(lonLatCoords));
        }
        mercatorCoords.push(x, y);
    }
    
    return mercatorCoords;
}

function lon2x(lon: number) {
    lon  = Number(lon);
    if (isNaN(lon)) {
        return null;
    }

    return lon * 20037508.3427892 / 180;
}

function lat2y(lat: number) {
    lat  = Number(lat);
    if(isNaN(lat)) {
        return null;
    }

    //var y = 180 / Math.PI * (2 * Math.atan(Math.exp((lat / 20037508.3427892) * Math.PI)) - Math.PI / 2);
    let y = Math.log(Math.tan((90 + lat) * Math.PI / 360)) / Math.PI * 20037508.3427892;
    y = Math.max(-20037508.3427892, Math.min(y, 20037508.3427892));

    return y;
}

/**
 * Helper method to extract a trailing number from any string
 * Examples:
 * - 'bubble_2659' -> 2659
 * - 'symbol_2659' -> 2659
 * - 'label_2659' -> 2659
 * - 'custom_prefix_123' -> 123
 * - 'whatever_42' -> 42
 * - 'justanumber42' -> 42
 * - '42' -> 42
 */
export function extractTrailingNumber(str: string): number | undefined {
    const match = str.match(/(\d+)$/);
    if (match && match[1]) {
        return parseInt(match[1], 10);
    }
    return undefined;
}